/* ==========================================================================
   Greasy Miniatures — page template behaviour
   Vanilla JS, no dependencies, no build step. Companion to greasy-theme.js
   (cart count, header height, auto-submitting selects) and
   greasy-sections.js (header, hero, rails).

   Everything here is an ENHANCEMENT over markup that already works:

     - The filter panel is a native <details> holding a GET form. This turns
       it into a drawer on a phone and auto-submits it on desktop.
     - The cart's quantity fields are real number inputs with an Update
       button. This adds the plus/minus steppers' behaviour and saves the
       trip to the button.

   If this file fails to load, filtering still filters and the cart still
   updates. That is the deal, and it is why nothing below builds markup.
   ========================================================================== */

(function () {
  'use strict';

  /* The width at which the filter panel stops being a drawer and becomes the
     sidebar. Must match the 1024px breakpoint in greasy-pages.css §6 — the
     CSS decides what it looks like, this decides how it behaves, and if the
     two disagree the panel is a fixed overlay on a desktop grid. */
  var DESKTOP = window.matchMedia('(min-width: 1024px)');

  /* ======================================================================
     FACETS — drawer on a phone, sidebar on a desktop
     ====================================================================== */

  function facets() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-gm-facets]'));
  }

  /* The panel ships `open` so that a browser with no script gets the filters
     expanded and complete. That is the wrong default on a phone WITH script,
     where it would push the products off the screen — so the first thing
     this does is take that state over. */
  function syncFacets() {
    var isDesktop = DESKTOP.matches;

    facets().forEach(function (details) {
      details.classList.toggle('gm-facets--drawer', !isDesktop);

      /* On desktop there is no toggle, so a closed panel would be an
         unreachable sidebar; on a phone an open one covers the products.
         This only runs on load and when the breakpoint is actually crossed,
         never on an ordinary resize, so it cannot fight the visitor for
         control of a drawer they have open. */
      details.open = isDesktop;
    });

    lockScroll(anyDrawerOpen());
  }

  function anyDrawerOpen() {
    if (DESKTOP.matches) return false;
    return facets().some(function (details) {
      return details.open;
    });
  }

  var scrollLocked = false;

  function lockScroll(shouldLock) {
    if (shouldLock === scrollLocked) return;
    scrollLocked = shouldLock;
    /* The same element greasy-sections.js locks for the mobile nav drawer,
       so the two can never disagree about which one owns the scrollbar. */
    document.documentElement.style.overflow = shouldLock ? 'hidden' : '';
  }

  function bindFacets(scope) {
    var root = scope || document;

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-gm-facets]'),
      function (details) {
        /* Guarded: the theme editor re-renders a section on every edit and
           init() runs again each time. */
        if (details.dataset.gmBound === 'true') return;
        details.dataset.gmBound = 'true';

        details.addEventListener('toggle', function () {
          lockScroll(anyDrawerOpen());
        });

        var closer = details.querySelector('[data-gm-facets-close]');
        if (closer) {
          closer.addEventListener('click', function () {
            details.open = false;
            var summary = details.querySelector('summary');
            /* Focus goes back to the control that opened the drawer, or it
               is left on a button that is no longer on screen. */
            if (summary) summary.focus();
          });
        }

        /* Auto-submit on desktop only. On a phone the drawer covers the
           page, and reloading it on every tick would close the drawer
           under the finger — there, "Show results" is the commit. */
        details.addEventListener('change', function (event) {
          if (!DESKTOP.matches) return;
          if (!event.target.matches('input[type="checkbox"]')) return;

          var form = details.closest('form');
          if (form) form.submit();
        });
      }
    );
  }

  function bindFacetGlobals() {
    /* Escape closes an open drawer. */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || DESKTOP.matches) return;

      facets().forEach(function (details) {
        if (!details.open) return;
        details.open = false;
        var summary = details.querySelector('summary');
        if (summary) summary.focus();
      });
    });

    /* Click away — which on a phone means the scrim, drawn as a
       pseudo-element on the panel, so there is no node to hang a listener
       on. Anything outside the panel counts. */
    document.addEventListener('pointerdown', function (event) {
      if (DESKTOP.matches) return;

      facets().forEach(function (details) {
        if (!details.open) return;
        var panel = details.querySelector('.gm-facets__panel');
        var summary = details.querySelector('summary');
        if (panel && panel.contains(event.target)) return;
        if (summary && summary.contains(event.target)) return;
        details.open = false;
      });
    });

    DESKTOP.addEventListener('change', syncFacets);
  }

  /* ======================================================================
     CART — quantity steppers
     ====================================================================== */

  /* One shared timer per form. Clicking "+" four times should be one round
     trip to Shopify, not four — and four would race each other, because
     each reply rewrites the same line. */
  var cartTimers = new WeakMap();

  function submitCart(form) {
    var pending = cartTimers.get(form);
    if (pending) window.clearTimeout(pending);

    cartTimers.set(
      form,
      window.setTimeout(function () {
        /* Submit through the Update button so the request carries the same
           parameters the no-script path sends. requestSubmit is what allows
           a submitter to be named; without it, click() does the same job. */
        var update = form.querySelector('[name="update"]');
        if (update && form.requestSubmit) {
          form.requestSubmit(update);
        } else if (update) {
          update.click();
        } else {
          form.submit();
        }
      }, 550)
    );
  }

  function bindCart(scope) {
    var root = scope || document;

    Array.prototype.forEach.call(
      root.querySelectorAll('[data-gm-cart]'),
      function (form) {
        if (form.dataset.gmBound === 'true') return;
        form.dataset.gmBound = 'true';

        form.addEventListener('click', function (event) {
          var step = event.target.closest('[data-gm-qty]');
          if (!step) return;

          var row = step.closest('.gm-qty');
          var input = row && row.querySelector('[data-gm-qty-input]');
          if (!input) return;

          var delta = parseInt(step.dataset.gmQty, 10) || 0;
          var next = (parseInt(input.value, 10) || 0) + delta;
          /* Zero is a real value — it is how a line is removed — so the
             floor is 0, not 1. */
          if (next < 0) next = 0;

          input.value = String(next);
          submitCart(form);
        });

        /* A typed quantity commits on blur, which is what `change` means on
           a number field. */
        form.addEventListener('change', function (event) {
          if (!event.target.matches('[data-gm-qty-input]')) return;
          submitCart(form);
        });
      }
    );
  }

  /* ======================================================================
     BOOT
     ====================================================================== */

  function init(scope) {
    bindFacets(scope);
    bindCart(scope);
    syncFacets();
  }

  function boot() {
    bindFacetGlobals();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor swaps a section's markup in place; the new nodes carry
     no bindings. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
